import { useMemo, useState } from 'react';
import { App, Badge, Button, Card, Popover, Select, Space, Tooltip, Typography } from 'antd';
import { DataTable } from '../../components/DataTable';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { StatusTag } from '../../components/StatusTag';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircle, FunnelSimple, Prohibit, WarningCircle } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import {
  REQUEST_STATES,
  REQUEST_TYPES,
  describeDeadline,
  isOpenRequest,
  isPinnedAutoCompleted,
  privacyApi,
  privacyKeys,
  sortPrivacyQueue,
  type DataSubjectRequest,
  type DataSubjectRequestState,
  type DataSubjectRequestType,
} from './api';
import { CompleteRequestModal } from './CompleteRequestModal';
import { RejectRequestModal } from './RejectRequestModal';
import { tokens } from '../../theme';

/** Option cho dropdown lọc — lấy nhãn từ chính bảng nhãn đang dùng ở cột, không đặt lại tay. */
const TYPE_OPTIONS = (Object.keys(REQUEST_TYPES) as DataSubjectRequestType[]).map((k) => ({
  value: k,
  label: REQUEST_TYPES[k].label,
}));
const STATE_OPTIONS = (Object.keys(REQUEST_STATES) as DataSubjectRequestState[]).map((k) => ({
  value: k,
  label: REQUEST_STATES[k],
}));

/**
 * `dd/MM HH:mm` — ghép tay thay vì để `Intl` tự xếp.
 *
 * `Intl` với locale `vi-VN` cho ra `21:36 06-08`: giờ đứng trước ngày, và ngày tháng nối
 * bằng dấu gạch. Người Việt đọc `06-08` là mùng 6 tháng 8 hay 8 tháng 6 đều được — mà
 * đây là cột hạn pháp lý, đọc nhầm là trễ hạn.
 */
function formatVnDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  return `${get('day')}/${get('month')} ${get('hour')}:${get('minute')}`;
}

export function PrivacyRequestsPage() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  // Gom mọi bộ lọc vào một nút "Lọc" (như trang Đánh giá bữa ăn): loại yêu cầu + trạng thái, lọc
  // client-side. Luôn tải TẤT CẢ phiếu để HintBanner đếm đúng số quá hạn/đang chờ dù đang lọc gì.
  const [typeFilter, setTypeFilter] = useState<DataSubjectRequestType>();
  const [statePick, setStatePick] = useState<DataSubjectRequestState>();
  const [completing, setCompleting] = useState<DataSubjectRequest | null>(null);
  const [rejecting, setRejecting] = useState<DataSubjectRequest | null>(null);
  const deadlineHint = useDismissibleHint('privacy-deadlines');

  const requestsQuery = useQuery({
    queryKey: privacyKeys.requests(undefined),
    queryFn: () => privacyApi.list(undefined),
    // Hàng đợi có hạn luật định — để màn hình mở cả buổi mà không tự làm mới thì
    // người trực nhìn vào một bức ảnh cũ
    refetchInterval: 60_000,
  });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['privacy-requests'] });

  const completeMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => privacyApi.complete(id, note),
    onSuccess: async (_, variables) => {
      const wasErasure = completing?.type === 'Erasure';
      await refresh();
      setCompleting(null);
      message.success(
        wasErasure
          ? 'Đã ẩn danh hồ sơ. Sổ cái điểm được giữ lại.'
          : 'Đã ghi nhận hoàn tất yêu cầu.',
      );
      void variables;
    },
    onError: (error) => reportError(error, 'Không hoàn tất được yêu cầu.'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => privacyApi.reject(id, reason),
    onSuccess: async () => {
      await refresh();
      setRejecting(null);
      message.success('Đã từ chối yêu cầu kèm lý do.');
    },
    onError: (error) => reportError(error, 'Không từ chối được yêu cầu.'),
  });

  const requests = useMemo(
    () => sortPrivacyQueue(requestsQuery.data ?? []),
    [requestsQuery.data],
  );
  const overdue = requests.filter((r) => r.isOverdue && isOpenRequest(r)).length;
  const pending = requests.filter(isOpenRequest).length;
  const pinnedAuto = requests.filter((r) => isPinnedAutoCompleted(r)).length;

  // Áp lọc (loại + trạng thái) lên tập đã tải. `overdue`/`pending` ở trên vẫn tính trên `requests`
  // gốc để HintBanner phản ánh đúng khối lượng thật, không đổi theo cái đang xem.
  const visibleRequests = useMemo(
    () =>
      requests.filter(
        (r) => (!typeFilter || r.type === typeFilter) && (!statePick || r.state === statePick),
      ),
    [requests, typeFilter, statePick],
  );

  const columns: ColumnsType<DataSubjectRequest> = [
    {
      // Hạn đứng cột ĐẦU vì đó là thứ quyết định làm cái nào trước
      title: 'Hạn phải trả lời',
      key: 'due',
      width: 190,
      render: (_, request) => {
        const done = request.state === 'Completed' || request.state === 'Rejected';
        const pinned = isPinnedAutoCompleted(request);
        return (
          <>
            <div
              style={{
                fontWeight: 600,
                color: request.isOverdue ? tokens.color.danger : undefined,
              }}
            >
              {/* Không chỉ dựa vào màu: có icon và có chữ "Quá hạn" */}
              {request.isOverdue && !done && (
                <WarningCircle size={14} weight="fill" style={{ marginRight: 4 }} />
              )}
              {done
                ? pinned
                  ? 'Đã xong trong hạn'
                  : '—'
                : describeDeadline(request.dueAt, request.isOverdue)}
            </div>
            <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
              {formatVnDateTime(request.dueAt)}
            </div>
          </>
        );
      },
    },
    {
      title: 'Loại yêu cầu',
      key: 'type',
      width: 280,
      render: (_, request) => {
        const meta = REQUEST_TYPES[request.type];
        const justAutoHandled = isPinnedAutoCompleted(request);
        return (
          <>
            <div style={{ fontWeight: 600 }}>
              {meta.label}
              {justAutoHandled && (
                <StatusTag color="blue" style={{ marginLeft: 6 }}>
                  Vừa tự xử lý
                </StatusTag>
              )}
              {meta.isIrreversible && (
                <StatusTag color="error" style={{ marginLeft: 6 }}>
                  Không lùi được
                </StatusTag>
              )}
            </div>
            <Tooltip title={meta.description}>
              <div
                style={{
                  fontSize: 12,
                  color: tokens.color.ink2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                }}
              >
                {meta.description}
              </div>
            </Tooltip>
          </>
        );
      },
    },
    {
      title: 'Khách',
      key: 'member',
      width: 180,
      render: (_, request) => (
        <>
          <div style={{ fontWeight: 600 }}>{request.memberName ?? '—'}</div>
          <div style={{ fontSize: 11.5, color: tokens.color.ink3, fontFamily: 'monospace' }}>
            {request.memberCode}
          </div>
        </>
      ),
    },
    {
      title: 'Nhận lúc',
      key: 'requestedAt',
      width: 130,
      render: (_, request) => (
        <span style={{ fontSize: 12.5 }}>{formatVnDateTime(request.requestedAt)}</span>
      ),
    },
    {
      title: 'Trạng thái',
      key: 'state',
      width: 130,
      render: (_, request) => {
        const tone =
          request.state === 'Completed'
            ? 'success'
            : request.state === 'Rejected'
              ? 'default'
              : request.isOverdue
                ? 'error'
                : 'processing';
        return <StatusTag color={tone}>{REQUEST_STATES[request.state]}</StatusTag>;
      },
    },
    {
      title: 'Ghi chú',
      key: 'note',
      render: (_, request) =>
        request.note ? (
          <span style={{ fontSize: 12.5 }}>{request.note}</span>
        ) : (
          <span style={{ color: tokens.color.ink3 }}>—</span>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_, request) => {
        const done = request.state === 'Completed' || request.state === 'Rejected';
        if (done) return <span style={{ color: tokens.color.ink3 }}>Đã xử lý</span>;

        return (
          <>
            <Button
              size="small"
              icon={<CheckCircle size={14} />}
              aria-label={`Hoàn tất ${REQUEST_TYPES[request.type].label} của ${request.memberCode}`}
              onClick={() => setCompleting(request)}
            >
              Hoàn tất
            </Button>
            <Button
              size="small"
              type="text"
              danger
              icon={<Prohibit size={14} />}
              style={{ marginLeft: 4 }}
              aria-label={`Từ chối ${REQUEST_TYPES[request.type].label} của ${request.memberCode}`}
              onClick={() => setRejecting(request)}
            >
              Từ chối
            </Button>
          </>
        );
      },
    },
  ];

  // Gom mọi bộ lọc vào MỘT nút "Lọc" trong thanh công cụ (như trang Đánh giá bữa ăn). Badge đếm số
  // bộ lọc đang thu hẹp; popover luôn dựng như nhau nên không dính lỗi "phải bấm bảng mới áp dụng".
  const activeFilterCount = (typeFilter ? 1 : 0) + (statePick ? 1 : 0);

  const resetFilters = () => {
    setTypeFilter(undefined);
    setStatePick(undefined);
  };

  const fieldLabel = (text: string) => (
    <span style={{ display: 'block', fontSize: 12, color: tokens.color.ink2, marginBottom: 4 }}>
      {text}
    </span>
  );

  const filterControl = (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <Space direction="vertical" size={12} style={{ width: 240 }}>
          <div>
            {fieldLabel('Loại yêu cầu')}
            <Select
              allowClear
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: '100%' }}
              placeholder="Mọi loại"
              options={TYPE_OPTIONS}
            />
          </div>
          <div>
            {fieldLabel('Trạng thái')}
            <Select
              allowClear
              value={statePick}
              onChange={setStatePick}
              style={{ width: '100%' }}
              placeholder="Mọi trạng thái"
              options={STATE_OPTIONS}
            />
          </div>
          <Button
            type="link"
            size="small"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            style={{ padding: 0, alignSelf: 'flex-start' }}
          >
            Đặt lại bộ lọc
          </Button>
        </Space>
      }
    >
      <Badge count={activeFilterCount} size="small" offset={[-4, 2]}>
        <Button icon={<FunnelSimple size={15} aria-hidden />} size="small">
          Lọc
        </Button>
      </Badge>
    </Popover>
  );

  return (
    <>
      <PageHeader
        title="Yêu cầu về dữ liệu cá nhân"
        subtitle="Rút đồng ý tự xử lý hiện trên Tất cả · hạn gần nhất lên đầu"
        hint={<HintToggle hint={deadlineHint} />}
      />
      <PageBody>
        {requestsQuery.isPending && <LoadingState />}

        {requestsQuery.isError && (
          <ErrorState error={requestsQuery.error} onRetry={() => void requestsQuery.refetch()} />
        )}

        {requestsQuery.isSuccess && (
          <>
            <HintBanner
              hint={deadlineHint}
              type={overdue > 0 ? 'error' : pending > 0 ? 'warning' : 'success'}
              message={
                overdue > 0
                  ? `${overdue} yêu cầu ĐÃ QUÁ HẠN`
                  : pending > 0
                    ? `${pending} yêu cầu đang chờ, chưa có cái nào quá hạn`
                    : 'Không có yêu cầu nào đang chờ'
              }
              description="Hạn do luật đặt: 2 ngày làm việc với rút đồng ý và hạn chế xử lý · 15 ngày với xem và sửa · 30 ngày với xóa tài khoản."
            />

            <Card styles={{ body: { paddingTop: 8 } }}>
              <DataTable
                title="Yêu cầu quyền riêng tư"
                subTitle={
                  pinnedAuto > 0
                    ? `${visibleRequests.length} yêu cầu · ${pinnedAuto} vừa tự xử lý`
                    : `${visibleRequests.length} yêu cầu`
                }
                onReload={() => void requestsQuery.refetch()}
                searchPlaceholder="Tìm theo tên, mã, ghi chú"
                // Cột "Khách" render ghép tên + mã nên heuristic đoán-cột không thấy — khai thẳng
                // chuỗi dò: tên, mã KH, ghi chú, và nhãn loại/trạng thái.
                searchText={(r) =>
                  [
                    r.memberName,
                    r.memberCode,
                    r.note,
                    REQUEST_TYPES[r.type].label,
                    REQUEST_STATES[r.state],
                  ]
                    .filter(Boolean)
                    .join(' ')
                }
                toolbarExtra={filterControl}
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={visibleRequests}
                pagination={false}
                // Cửa sổ hẹp thì cuộn ngang, không bóp cột
                scroll={{ x: 1240 }}
                locale={{
                  emptyText:
                    typeFilter || statePick
                      ? 'Không có yêu cầu nào khớp bộ lọc.'
                      : 'Chưa có yêu cầu nào.',
                }}
                // Tô nền dòng quá hạn, kèm icon và chữ ở cột hạn — không dùng riêng màu.
                //
                // Đặt style qua `onRow` thay vì class CSS: `index.css` ghi rõ token màu nằm
                // ở `theme.ts`, và viết cứng một mã màu trong CSS là mở đường cho hai bộ màu
                // lệch nhau.
                onRow={(request) => {
                  if (request.isOverdue && isOpenRequest(request)) {
                    return { style: { background: tokens.color.danger50 } };
                  }
                  if (isPinnedAutoCompleted(request)) {
                    return { style: { background: tokens.color.info50 } };
                  }
                  return {};
                }}
              />
            </Card>

            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12.5, marginTop: tokens.space.sectionGap }}
            >
              Yêu cầu <strong>xem dữ liệu</strong>, <strong>sửa dữ liệu</strong> và{' '}
              <strong>rút lại đồng ý</strong> được hệ thống thực hiện ngay khi khách bấm trong ứng
              dụng. Chúng hiện trên <strong>Tất cả</strong> (mặc định) — rút đồng ý còn trong hạn
              2 ngày làm việc được ghim đầu danh sách kèm badge <strong>Vừa tự xử lý</strong>. Lọc{' '}
              <strong>Cần xử lý</strong> chỉ còn phiếu chờ người: chủ yếu là{' '}
              <strong>xóa tài khoản</strong>.
            </Typography.Paragraph>
          </>
        )}
      </PageBody>

      <CompleteRequestModal
        request={completing}
        submitting={completeMutation.isPending}
        onCancel={() => setCompleting(null)}
        onSubmit={(note) => {
          if (completing) completeMutation.mutate({ id: completing.id, note });
        }}
      />

      <RejectRequestModal
        request={rejecting}
        submitting={rejectMutation.isPending}
        onCancel={() => setRejecting(null)}
        onSubmit={(reason) => {
          if (rejecting) rejectMutation.mutate({ id: rejecting.id, reason });
        }}
      />
    </>
  );
}
